import { API_BASE } from '@/shared/services/api';
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/shared/contexts/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/shared/utils';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { Label } from '@/shared/components/ui/label';
import { Progress } from '@/shared/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/components/ui/table';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import {
  Navigation, Truck, Clock, MapPin, AlertTriangle, CheckCircle, Settings,
  RefreshCw, Play, Zap, Route, Timer, TrendingUp, TrendingDown,
  Activity, BarChart3, Map, Compass, CloudSun, Car, GitBranch,
  ExternalLink, Info, Shield, Key, TestTube, ArrowRight, Gauge } from
'lucide-react';

const API_URL = API_BASE;

// Traffic condition configurations
const TRAFFIC_CONDITIONS = {
  clear: { label: 'Clear', color: 'bg-emerald-500', textColor: 'text-emerald-500', icon: CheckCircle },
  light: { label: 'Light', color: 'bg-green-400', textColor: 'text-green-400', icon: TrendingUp },
  moderate: { label: 'Moderate', color: 'bg-amber-500', textColor: 'text-amber-500', icon: Activity },
  heavy: { label: 'Heavy', color: 'bg-orange-500', textColor: 'text-orange-500', icon: TrendingDown },
  severe: { label: 'Severe', color: 'bg-red-500', textColor: 'text-red-500', icon: AlertTriangle },
  blocked: { label: 'Blocked', color: 'bg-red-700', textColor: 'text-red-700', icon: AlertTriangle }
};

// Health status configurations
const HEALTH_STATUS = {
  healthy: { label: 'Healthy', color: 'bg-emerald-500', icon: CheckCircle },
  degraded: { label: 'Degraded', color: 'bg-amber-500', icon: AlertTriangle },
  warning: { label: 'Warning', color: 'bg-orange-500', icon: AlertTriangle },
  critical: { label: 'Critical', color: 'bg-red-500', icon: AlertTriangle },
  unknown: { label: 'Unknown', color: 'bg-gray-500', icon: Info }
};

// Provider configurations
const TRAFFIC_PROVIDERS = {
  mock: {
    name: 'Mock (Testing)',
    icon: TestTube,
    color: 'text-gray-500',
    features: { realTimeTraffic: false, etaCalculation: true, routeOptimization: true, polylineSupport: false },
    docs: null
  },
  google: {
    name: 'Google Maps Platform',
    icon: Map,
    color: 'text-blue-500',
    features: { realTimeTraffic: true, etaCalculation: true, routeOptimization: true, polylineSupport: true },
    docs: 'https://developers.google.com/maps/documentation/directions'
  },
  here: {
    name: 'HERE Maps',
    icon: Compass,
    color: 'text-green-500',
    features: { realTimeTraffic: true, etaCalculation: true, routeOptimization: true, polylineSupport: true },
    docs: 'https://developer.here.com/documentation/routing-api'
  },
  mapbox: {
    name: 'Mapbox',
    icon: Navigation,
    color: 'text-violet-500',
    features: { realTimeTraffic: true, etaCalculation: true, routeOptimization: true, polylineSupport: true },
    docs: 'https://docs.mapbox.com/api/navigation/directions/'
  },
  openroute: {
    name: 'OpenRouteService',
    icon: Route,
    color: 'text-orange-500',
    features: { realTimeTraffic: false, etaCalculation: true, routeOptimization: true, polylineSupport: true },
    docs: 'https://openrouteservice.org/dev/#/api-docs'
  }
};

// Optimization Result Card
const OptimizationResultCard = ({ result }) => {const { t } = useTranslation("wms");
  if (!result) return null;

  const delayStatus = result.total_delay_minutes > 15 ?
  'severe' :
  result.total_delay_minutes > 5 ?
  'moderate' :
  'clear';

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Route className="h-5 w-5" />{t("routeOptimizationResults", "Route Optimization Results")}

        </CardTitle>
        <CardDescription>{t("route", "Route:")}
          {result.route_id}{t("optimized", "\u2022 Optimized:")}{new Date(result.optimized_at).toLocaleString()}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-xs text-muted-foreground">{t("totalDistance", "Total Distance")}</p>
            <p className="text-lg font-bold">{(result.total_distance_meters / 1000).toFixed(1)}{t("km", "km")}</p>
          </div>
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-xs text-muted-foreground">{t("baseDuration", "Base Duration")}</p>
            <p className="text-lg font-bold">{(result.total_duration_minutes ?? 0).toFixed(0)}{t("min", "min")}</p>
          </div>
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-xs text-muted-foreground">{t("withTraffic", "With Traffic")}</p>
            <p className="text-lg font-bold">{(result.total_duration_with_traffic_minutes ?? 0).toFixed(0)}{t("min", "min")}</p>
          </div>
          <div className={cn('p-3 rounded-lg', TRAFFIC_CONDITIONS[delayStatus].color + '/10')}>
            <p className="text-xs text-muted-foreground">{t("totalDelay", "Total Delay")}</p>
            <p className={cn('text-lg font-bold', TRAFFIC_CONDITIONS[delayStatus].textColor)}>
              +{(result.total_delay_minutes ?? 0).toFixed(0)}{t("min", "min")}
            </p>
          </div>
        </div>

        {/* Suggestions */}
        {result.suggestions && result.suggestions.length > 0 &&
        <div className="space-y-2">
            <Label>{t("optimizationSuggestions", "Optimization Suggestions")}</Label>
            {result.suggestions.map((suggestion, i) =>
          <Alert key={i} variant={suggestion.priority === 'critical' ? 'destructive' : 'default'}>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="ml-2">
                  <span className="font-medium">{suggestion.type.replace('_', ' ').toUpperCase()}: </span>
                  {suggestion.message}
                  {suggestion.estimated_time_savings_minutes > 0 &&
              <span className="text-emerald-500 ml-2">{t("potentialSavings", "(Potential savings:")}
                {suggestion.estimated_time_savings_minutes}{t("min", "min)")}
              </span>
              }
                </AlertDescription>
              </Alert>
          )}
          </div>
        }

        {/* ETAs by Stop */}
        {result.eta_by_stop && Object.keys(result.eta_by_stop).length > 0 &&
        <div className="space-y-2">
            <Label>{t("estimatedArrivalTimes", "Estimated Arrival Times")}</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(result.eta_by_stop).map(([stopId, eta]) =>
            <div key={stopId} className="flex items-center justify-between p-2 bg-muted rounded-lg text-sm">
                  <span className="font-mono">{stopId}</span>
                  <span className="text-muted-foreground">{new Date(eta).toLocaleTimeString()}</span>
                </div>
            )}
            </div>
          </div>
        }

        {/* Route Segments */}
        {result.segments && result.segments.length > 0 &&
        <div className="space-y-2">
            <Label>{t("routeSegments", "Route Segments")}</Label>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("toStop", "To Stop")}</TableHead>
                  <TableHead>{t("distance", "Distance")}</TableHead>
                  <TableHead>{t("duration", "Duration")}</TableHead>
                  <TableHead>{t("traffic", "Traffic")}</TableHead>
                  <TableHead>{t("delay", "Delay")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.segments.map((segment, i) => {
                const condition = TRAFFIC_CONDITIONS[segment.traffic_condition] || TRAFFIC_CONDITIONS.clear;
                const ConditionIcon = condition.icon;
                return (
                  <TableRow key={i}>
                      <TableCell className="font-mono">{segment.to_stop_id}</TableCell>
                      <TableCell>{(segment.distance_meters / 1000).toFixed(1)}{t("km", "km")}</TableCell>
                      <TableCell>{Math.round(segment.duration_seconds / 60)}{t("min", "min")}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={condition.textColor}>
                          <ConditionIcon className="h-3 w-3 mr-1" />
                          {condition.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {segment.delay_seconds > 0 ?
                      <span className="text-red-500">+{Math.round(segment.delay_seconds / 60)}{t("min", "min")}</span> :

                      <span className="text-emerald-500">{t("none", "None")}</span>
                      }
                      </TableCell>
                    </TableRow>);

              })}
              </TableBody>
            </Table>
          </div>
        }
      </CardContent>
    </Card>);

};

// Provider Card Component
const ProviderCard = ({ providerId, provider, isActive, isConfigured, apiKeyMasked, onSelect, onTest }) => {const { t } = useTranslation("wms");
  const ProviderIcon = provider.icon;

  return (
    <Card className={cn(
      'cursor-pointer transition-all hover:shadow-md',
      isActive && 'ring-2 ring-primary'
    )}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center bg-muted', provider.color)}>
              <ProviderIcon className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold">{provider.name}</h3>
              <div className="flex items-center gap-2 mt-1">
                {isConfigured ?
                <Badge variant="outline" className="text-emerald-500 text-xs">
                    <CheckCircle className="h-3 w-3 mr-1" />{t("configured", "Configured")}

                </Badge> :
                providerId !== 'mock' && providerId !== 'openroute' ?
                <Badge variant="outline" className="text-amber-500 text-xs">
                    <Key className="h-3 w-3 mr-1" />{t("apiKeyRequired", "API Key Required")}

                </Badge> :

                <Badge variant="outline" className="text-emerald-500 text-xs">
                    <CheckCircle className="h-3 w-3 mr-1" />{t("ready", "Ready")}

                </Badge>
                }
                {isActive &&
                <Badge className="text-xs bg-primary">{t("active", "Active")}</Badge>
                }
              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
          <div className="flex items-center gap-1">
            {provider.features.realTimeTraffic ?
            <CheckCircle className="h-3 w-3 text-emerald-500" /> :

            <AlertTriangle className="h-3 w-3 text-amber-500" />
            }
            <span>{t("realtimeTraffic", "Real-time Traffic")}</span>
          </div>
          <div className="flex items-center gap-1">
            {provider.features.etaCalculation ?
            <CheckCircle className="h-3 w-3 text-emerald-500" /> :

            <AlertTriangle className="h-3 w-3 text-amber-500" />
            }
            <span>{t("etaCalculation", "ETA Calculation")}</span>
          </div>
          <div className="flex items-center gap-1">
            {provider.features.routeOptimization ?
            <CheckCircle className="h-3 w-3 text-emerald-500" /> :

            <AlertTriangle className="h-3 w-3 text-amber-500" />
            }
            <span>{t("routeOptimization", "Route Optimization")}</span>
          </div>
          <div className="flex items-center gap-1">
            {provider.features.polylineSupport ?
            <CheckCircle className="h-3 w-3 text-emerald-500" /> :

            <AlertTriangle className="h-3 w-3 text-amber-500" />
            }
            <span>{t("polylineSupport", "Polyline Support")}</span>
          </div>
        </div>

        {/* API Key Display */}
        {apiKeyMasked &&
        <div className="mt-3 p-2 bg-muted rounded text-xs font-mono">{t("apiKey", "API Key:")}
          {apiKeyMasked}
          </div>
        }

        {/* Actions */}
        <div className="flex items-center gap-2 mt-4">
          <Button
            variant={isActive ? 'default' : 'outline'}
            size="sm"
            className="flex-1"
            onClick={() => onSelect(providerId)}
            disabled={!isConfigured && providerId !== 'mock' && providerId !== 'openroute'}>
            
            {isActive ? 'Active' : 'Select'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onTest(providerId)}>
            
            <TestTube className="h-4 w-4" />
          </Button>
          {provider.docs &&
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.open(provider.docs, '_blank')}>
            
              <ExternalLink className="h-4 w-4" />
            </Button>
          }
        </div>
      </CardContent>
    </Card>);

};

// Main Route Optimization Component
export default function RouteOptimization() {
  const { t } = useTranslation(['wms', 'common']);
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [trafficSettings, setTrafficSettings] = useState(null);
  const [optimizationResult, setOptimizationResult] = useState(null);
  const [routeHealthResults, setRouteHealthResults] = useState([]);
  const [optimizing, setOptimizing] = useState(false);
  const [testingProvider, setTestingProvider] = useState(null);

  // Form states
  const [routeId, setRouteId] = useState('');
  const [currentLat, setCurrentLat] = useState('');
  const [currentLng, setCurrentLng] = useState('');

  const fetchTrafficSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/traffic`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setTrafficSettings(data);
    } catch (error) {
      console.error('Error fetching traffic settings:', error);
      toast.error('Failed to fetch settings');
    }
  }, [token]);

  const fetchTrafficStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/route-optimization/traffic/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      return await response.json();
    } catch (error) {
      console.error('Error fetching traffic status:', error);
      return null;
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchTrafficSettings();
      setLoading(false);
    };
    loadData();
  }, [fetchTrafficSettings]);

  const handleSelectProvider = async (providerId) => {
    try {
      const response = await fetch(`${API_URL}/api/settings/traffic/provider?provider=${providerId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(`Traffic provider set to ${TRAFFIC_PROVIDERS[providerId].name}`);
        fetchTrafficSettings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to set provider');
      }
    } catch (error) {
      toast.error('Failed to set provider');
    }
  };

  const handleTestProvider = async (providerId) => {
    setTestingProvider(providerId);
    try {
      const response = await fetch(`${API_URL}/api/settings/traffic/test?provider=${providerId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();

      if (result.success) {
        toast.success(
          `${TRAFFIC_PROVIDERS[providerId].name} test successful! ` +
          `Distance: ${(result.test_result.distance_meters / 1000).toFixed(1)}km, ` +
          `Duration: ${Math.round(result.test_result.duration_seconds / 60)}min`
        );
      } else {
        toast.error(`Test failed: ${result.error}`);
      }
    } catch (error) {
      toast.error('Test failed');
    } finally {
      setTestingProvider(null);
    }
  };

  const handleOptimizeRoute = async () => {
    if (!routeId) {
      toast.error('Please enter a Route ID');
      return;
    }

    setOptimizing(true);
    try {
      const body = {
        route_id: routeId,
        current_position: currentLat && currentLng ? {
          latitude: parseFloat(currentLat),
          longitude: parseFloat(currentLng)
        } : null
      };

      const response = await fetch(`${API_URL}/api/route-optimization/optimize`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const result = await response.json();
        setOptimizationResult(result);
        toast.success('Route optimization complete!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Optimization failed');
      }
    } catch (error) {
      toast.error('Optimization failed');
    } finally {
      setOptimizing(false);
    }
  };

  const handleCheckRouteHealth = async () => {
    if (!routeId) {
      toast.error('Please enter a Route ID');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/route-optimization/health/${routeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        setRouteHealthResults([result, ...routeHealthResults.slice(0, 4)]);

        const healthConfig = HEALTH_STATUS[result.health] || HEALTH_STATUS.unknown;
        toast[result.health === 'healthy' ? 'success' : 'warning'](
          `Route ${result.route_id}: ${healthConfig.label} - ${(result.total_delay_minutes ?? 0).toFixed(0)}min delay`
        );
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Health check failed');
      }
    } catch (error) {
      toast.error('Health check failed');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="route-optimization-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Navigation className="h-7 w-7 text-primary" />
            {t('wms:routeOptimization.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('wms:routeOptimization.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-sm">
            <Gauge className="h-4 w-4 mr-1" />
            {t('wms:routeOptimization.providers.active')}: {trafficSettings?.current_provider || 'mock'}
          </Badge>
          <Button variant="outline" size="sm" onClick={fetchTrafficSettings}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('common:refresh')}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="optimize" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="optimize">
            <Zap className="h-4 w-4 mr-2" />
            {t('wms:routeOptimization.tabs.optimize')}
          </TabsTrigger>
          <TabsTrigger value="providers">
            <Settings className="h-4 w-4 mr-2" />
            {t('wms:routeOptimization.tabs.providers')}
          </TabsTrigger>
          <TabsTrigger value="health">
            <Activity className="h-4 w-4 mr-2" />
            {t('wms:routeOptimization.tabs.health')}
          </TabsTrigger>
        </TabsList>

        {/* Optimize Tab */}
        <TabsContent value="optimize" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('wms:routeOptimization.optimize.title')}</CardTitle>
              <CardDescription>
                {t('wms:routeOptimization.optimize.subtitle')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{t('wms:routeOptimization.optimize.routeId')} *</Label>
                  <Input
                    value={routeId}
                    onChange={(e) => setRouteId(e.target.value)}
                    placeholder="RTE-001"
                    data-testid="route-id-input" />
                  
                </div>
                <div className="space-y-2">
                  <Label>{t('wms:routeOptimization.optimize.currentLatitude')}</Label>
                  <Input
                    value={currentLat}
                    onChange={(e) => setCurrentLat(e.target.value)}
                    placeholder="-23.5505"
                    type="number"
                    step="any" />
                  
                </div>
                <div className="space-y-2">
                  <Label>{t('wms:routeOptimization.optimize.currentLongitude')}</Label>
                  <Input
                    value={currentLng}
                    onChange={(e) => setCurrentLng(e.target.value)}
                    placeholder="-46.6333"
                    type="number"
                    step="any" />
                  
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleOptimizeRoute}
                  disabled={optimizing || !routeId}
                  data-testid="optimize-route-btn">
                  
                  {optimizing ?
                  <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      {t('wms:routeOptimization.optimize.optimizing')}
                    </> :

                  <>
                      <Zap className="h-4 w-4 mr-2" />
                      {t('wms:routeOptimization.optimize.optimizeRoute')}
                    </>
                  }
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCheckRouteHealth}
                  disabled={!routeId}>
                  
                  <Activity className="h-4 w-4 mr-2" />
                  {t('wms:routeOptimization.optimize.healthCheck')}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Optimization Results */}
          {optimizationResult &&
          <OptimizationResultCard result={optimizationResult} />
          }

          {/* Recent Health Checks */}
          {routeHealthResults.length > 0 &&
          <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">{t('wms:routeOptimization.health.recentChecks')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {routeHealthResults.map((health, i) => {
                  const healthConfig = HEALTH_STATUS[health.health] || HEALTH_STATUS.unknown;
                  const HealthIcon = healthConfig.icon;
                  return (
                    <div
                      key={i}
                      className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      
                        <div className="flex items-center gap-3">
                          <div className={cn('w-8 h-8 rounded-full flex items-center justify-center', healthConfig.color + '/20')}>
                            <HealthIcon className={cn('h-4 w-4', healthConfig.color.replace('bg-', 'text-'))} />
                          </div>
                          <div>
                            <span className="font-medium">{health.route_id}</span>
                            <span className="text-muted-foreground ml-2 text-sm">
                              {health.pending_stops}{t("stops", "stops \u2022")}{health.suggestions_count}{t("suggestions", "suggestions")}
                          </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant="outline" className={healthConfig.color.replace('bg-', 'text-')}>
                            {healthConfig.label}
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1">
                            +{(health.total_delay_minutes ?? 0).toFixed(0)}{t("minDelay", "min delay")}
                        </p>
                        </div>
                      </div>);

                })}
                </div>
              </CardContent>
            </Card>
          }
        </TabsContent>

        {/* Providers Tab */}
        <TabsContent value="providers" className="space-y-6">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>{t("selectATraffic", "Select a traffic data provider below. API keys should be configured in the backend environment variables.\n              The active provider will be used for all route optimization operations.")}


            </AlertDescription>
          </Alert>

          {loading ?
          <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div> :

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {trafficSettings?.providers?.map((provider) => {
              const providerConfig = TRAFFIC_PROVIDERS[provider.id];
              if (!providerConfig) return null;

              return (
                <ProviderCard
                  key={provider.id}
                  providerId={provider.id}
                  provider={providerConfig}
                  isActive={trafficSettings.current_provider === provider.id}
                  isConfigured={provider.configured}
                  apiKeyMasked={provider.api_key_masked}
                  onSelect={handleSelectProvider}
                  onTest={handleTestProvider} />);


            })}
            </div>
          }

          {/* Configuration Guide */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />{t("apiKeyConfiguration", "API Key Configuration")}

              </CardTitle>
              <CardDescription>{t("addTheseEnvironment", "Add these environment variables to your backend/.env file")}

              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 font-mono text-sm">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-muted-foreground">{t("googleMapsPlatform", "# Google Maps Platform")}</p>
                  <p>{t("googlemapsapikeyyourgoogleapikey", "GOOGLE_MAPS_API_KEY=your_google_api_key")}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-muted-foreground">{t("hereMaps", "# HERE Maps")}</p>
                  <p>{t("hereapikeyyourhereapikey", "HERE_API_KEY=your_here_api_key")}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-muted-foreground">{t("mapbox", "# Mapbox")}</p>
                  <p>{t("mapboxapikeyyourmapboxapikey", "MAPBOX_API_KEY=your_mapbox_api_key")}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-muted-foreground">{t("openrouteserviceOptionalFree", "# OpenRouteService (optional, free tier available)")}</p>
                  <p>{t("openrouteapikeyyouropenrouteapikey", "OPENROUTE_API_KEY=your_openroute_api_key")}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-muted-foreground">{t("setActiveProvider", "# Set active provider")}</p>
                  <p>{t("trafficprovidergoogleOrHere", "TRAFFIC_PROVIDER=google  # or: here, mapbox, openroute, mock")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Health Tab */}
        <TabsContent value="health" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t("batchHealthCheck", "Batch Health Check")}</CardTitle>
              <CardDescription>{t("checkTheHealth", "Check the health of multiple routes at once")}

              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t("routeIdsCommaseparated", "Route IDs (comma-separated)")}</Label>
                <Input
                  value={routeId}
                  onChange={(e) => setRouteId(e.target.value)}
                  placeholder="RTE-001, RTE-002, RTE-003" />
                
              </div>
              <Button onClick={handleCheckRouteHealth} disabled={!routeId}>
                <Activity className="h-4 w-4 mr-2" />{t("checkHealth", "Check Health")}

              </Button>
            </CardContent>
          </Card>

          {/* Health Legend */}
          <Card>
            <CardHeader>
              <CardTitle>{t("healthStatusLegend", "Health Status Legend")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {Object.entries(HEALTH_STATUS).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <div key={key} className="flex items-center gap-2">
                      <div className={cn('w-8 h-8 rounded-full flex items-center justify-center', config.color + '/20')}>
                        <Icon className={cn('h-4 w-4', config.color.replace('bg-', 'text-'))} />
                      </div>
                      <span className="text-sm font-medium">{config.label}</span>
                    </div>);

                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>);

}